import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = "G-WRG9CN0VYF";
const COOKIE_KEY = "cookie-consent";

type ConsentPrefs = { analytics?: boolean; marketing?: boolean };

function detectGPC(): boolean {
  try {
    return (
      typeof navigator !== "undefined" &&
      (navigator as unknown as { globalPrivacyControl?: boolean })
        .globalPrivacyControl === true
    );
  } catch {
    return false;
  }
}

function getConsent(): ConsentPrefs {
  // GPC signal wins over any missing preference until the user explicitly
  // sets one in the cookie preferences dialog.
  try {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) {
      return detectGPC() ? { analytics: false, marketing: false } : {};
    }
    return JSON.parse(stored) as ConsentPrefs;
  } catch {
    return {};
  }
}

function updateConsent(prefs: ConsentPrefs) {
  if (typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    analytics_storage: prefs.analytics ? "granted" : "denied",
    ad_storage: prefs.marketing ? "granted" : "denied",
    ad_user_data: prefs.marketing ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
  });
}

function sendPageView(path: string) {
  if (typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: document.title,
  });
}

declare global {
  interface Window {
    dataLayer: unknown[] | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
  }
}

export function GoogleAnalytics() {
  const location = useLocation();
  const isFirstRender = useRef(true);

  // Sync consent state with gtag whenever the user updates preferences
  useEffect(() => {
    const handleConsentChange = () => updateConsent(getConsent());
    const handleStorage = (e: StorageEvent) => {
      if (e.key === COOKIE_KEY) handleConsentChange();
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener("cookie-consent-updated", handleConsentChange);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("cookie-consent-updated", handleConsentChange);
    };
  }, []);

  // Track SPA route changes (gtag respects consent state automatically)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
    sendPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Reference GA_ID so it's preserved as a build-time constant for any future use
  void GA_ID;

  return null;
}
